/**
 * Notification Template Types
 *
 * Defines types for notification templates used in check-in nudge system.
 *
 * @see WI-036 Nudge Notification Templates
 */

/**
 * Notification template types (EXHAUSTIVE for Phase 3)
 */
export enum NotificationTemplateType {
  FIRST_NUDGE = 'first_nudge',
  SECOND_NUDGE = 'second_nudge',
  FINAL_WARNING = 'final_warning',
  SNOOZE_CONFIRM = 'snooze_confirm',
  CHECK_IN_SUCCESS = 'check_in_success'
}

/**
 * Urgency levels for notification styling
 */
export enum UrgencyLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Template with localization support
 */
export interface NotificationTemplate {
  id: string;
  type: NotificationTemplateType;
  urgencyLevel: UrgencyLevel;
  title: LocalizedString;
  body: LocalizedString;
  actions: NotificationAction[];
  sound: NotificationSound;
  vibrationPattern: number[];
  badge: BadgeUpdate;
  color: string;
  icon: string;
  category: string;
}

/**
 * Localized string with fallback
 */
export interface LocalizedString {
  key: string;
  fallback: string;
}

/**
 * Notification action button
 */
export interface NotificationAction {
  id: string;
  titleKey: string;
  titleFallback: string;
  style: 'default' | 'destructive' | 'primary';
  requiresAuth: boolean;
  launchApp: boolean;
}

/**
 * Sound configuration
 */
export interface NotificationSound {
  name: string;
  critical: boolean;
  volume: number;
}

/**
 * Badge update instruction
 */
export interface BadgeUpdate {
  type: 'set' | 'increment';
  value: number;
}

/**
 * Context for template rendering
 */
export interface TemplateContext {
  checkIn: {
    id: string;
    note: string | null;
    triggerTime: Date;
    graceWindowMinutes: number;
    contactCount: number;
  };
  user: {
    name: string;
    locale: string;
    timezone: string;
  };
  timing: {
    minutesOverdue: number;
    minutesUntilEscalation: number;
    isLastChance: boolean;
  };
}

/**
 * Rendered notification ready for delivery
 */
export interface RenderedNotification {
  title: string;
  body: string;
  data: {
    type: string;
    checkInId: string;
    urgency: string;
    timestamp: string;
  };
  ios: IOSPayload;
  android: AndroidPayload;
}

/**
 * iOS-specific APNS payload
 */
export interface IOSPayload {
  aps: {
    badge: number;
    sound: string | { critical: number; name: string; volume: number };
    category: string;
    threadId: string;
    interruptionLevel?: 'passive' | 'active' | 'time-sensitive' | 'critical';
  };
}

/**
 * Android-specific FCM payload
 */
export interface AndroidPayload {
  priority: 'high' | 'normal';
  notification: {
    channelId: string;
    icon: string;
    color: string;
    sound: string;
    vibrate: number[];
    notificationPriority: number;
  };
}
